import {
  archiveRun,
  createSave,
  deleteGame,
  exportSave,
  importSave,
  loadGame,
  reduce,
  saveGame,
  type Action,
  type LossMode,
  type SaveDoc,
} from '@bratva/state';
import { play } from './sound.js';
import { allTerritories } from '@bratva/content';
import { bribeOutPrice } from '@bratva/rules';
import { territoriesAtRisk } from '@bratva/state';
import { raidIfCaught, raidIfHotOvernight } from './heat.js';
import { courtActions, healthAction } from './justice.js';
import { questActions } from './quests.js';

/**
 * Единственный держатель состояния. Сцена и интерфейс только шлют сюда действия
 * и читают снимок — правило «состояние правится через reducer» держится этим.
 */
const SLOT_KEY = 'bratva:slot';

/** Правила удержания: по ним ночью решается, не отжали ли район обратно. */
const HOLD_RULES = Object.fromEntries(
  allTerritories().map((entry) => [
    entry.id,
    { retakeAfterDays: entry.retakeAfterDays, holdAvtoritet: entry.holdAvtoritet, title: entry.title },
  ]),
);
const AUTOSAVE_DELAY_MS = 600;

type Listener = () => void;

class GameStore {
  private doc: SaveDoc;
  private slot = readSlot();
  private readonly listeners = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(initial: SaveDoc) {
    this.doc = initial;
  }

  get currentSlot(): number {
    return this.slot;
  }

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): SaveDoc => this.doc;

  /**
   * Каждое действие проходит через одну воронку, поэтому проверки на облаву,
   * больничку и суд не могут быть забыты в новом месте вызова: они живут
   * здесь, а не в кнопках.
   */
  dispatch(action: Action): void {
    // Прохождение закрыто — принимаем только «начать заново».
    if (this.doc.outcome !== 'active') return;
    if (action.type === 'sleep') {
      // Сначала ночная облава по нынешнему палеву, и только потом утро.
      const night = raidIfHotOvernight(this.doc);
      const evening = night ? reduce(this.doc, night) : this.doc;
      // Доля с территорий считается по контенту здесь: reducer про контент не знает.
      let morning = reduce(evening, { ...action, territoryBonus: territoryBonus(evening) });

      // Территорию мало отжать — её надо держать авторитетом.
      for (const lost of territoriesAtRisk(morning, HOLD_RULES)) {
        morning = reduce(morning, { type: 'loseTerritory', territoryId: lost.id, territoryTitle: lost.title });
      }

      // Суд не ждёт: если день суда наступил, а вопрос не решён — срок.
      const judged = this.settleCourt(morning);
      this.replace(this.settleQuests(judged));
      return;
    }

    let next = reduce(this.doc, action);
    if (action.type !== 'raid') {
      const caught = raidIfCaught(next);
      if (caught) next = reduce(next, caught);
    }
    next = this.settleHealth(next);
    cue(action, next);
    this.replace(this.settleQuests(next));
  }

  /** Переключить слот: каждое дело живёт своей жизнью. */
  async useSlot(slot: number): Promise<void> {
    this.slot = slot;
    writeSlot(slot);
    const loaded = await loadGame(slot);
    this.replace(loaded ?? createSave(freshSeed()));
    this.settleOnLoad();
  }

  /** Новое дело в текущем слоте: режим выбирается один раз и навсегда. */
  async startNew(mode: LossMode): Promise<void> {
    await deleteGame(this.slot);
    this.replace(createSave(freshSeed(), mode));
    this.settleOnLoad();
  }

  /** Закрытое прохождение уезжает в архив со статистикой. */
  async archiveIfFinished(): Promise<void> {
    if (this.doc.outcome === 'active') return;
    await archiveRun({
      seed: this.doc.seed,
      mode: this.doc.mode,
      outcome: this.doc.outcome,
      day: this.doc.day,
      level: this.doc.player.level,
      cash: this.doc.player.cash,
      territories: this.doc.territories.length,
      finishedAt: Date.now(),
    });
  }

  /** Здоровье в ноль: больничка, а в жёстком режиме — могила. */
  private settleHealth(doc: SaveDoc): SaveDoc {
    const action = healthAction(doc);
    return action ? reduce(doc, action) : doc;
  }

  /** День суда: логика в game/justice.ts, здесь только применение. */
  private settleCourt(doc: SaveDoc): SaveDoc {
    let current = doc;
    for (const action of courtActions(doc)) current = reduce(current, action);
    return current;
  }

  /** Во сколько встанет закрыть дело прямо сейчас. */
  courtPrice(): number {
    return bribeOutPrice(this.doc.justice, this.doc.npc['smotryashiy'] ?? 0);
  }

  /**
   * Квесты доигрываются сами: шаг закрывается, как только выполнено условие.
   * Цикл — потому что одна покупка может закрыть шаг и сразу весь квест,
   * а награда за него — открыть следующий.
   */
  private settleQuests(doc: SaveDoc): SaveDoc {
    let current = doc;
    for (let pass = 0; pass < 8; pass++) {
      const actions = questActions(current);
      if (actions.length === 0) break;
      for (const action of actions) current = reduce(current, action);
    }
    return current;
  }

  /** Первый заход в игру: квесты без условий должны открыться сразу. */
  settleOnLoad(): void {
    this.replace(this.settleQuests(this.doc));
  }

  replace(doc: SaveDoc): void {
    this.doc = doc;
    for (const listener of this.listeners) listener();
    this.scheduleAutosave();
  }

  toFile(): string {
    return exportSave(this.doc);
  }

  fromFile(text: string): void {
    this.replace(importSave(text));
  }

  private scheduleAutosave(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void saveGame(this.slot, this.doc).catch((error) => console.warn('Сейв не записался:', error));
    }, AUTOSAVE_DELAY_MS);
  }
}

/** Сумма долей со всех удерживаемых территорий. */
function territoryBonus(doc: SaveDoc): number {
  const bonuses = new Map(allTerritories().map((entry) => [entry.id, entry.incomeBonus]));
  return doc.territories.reduce((sum, held) => sum + (bonuses.get(held.id) ?? 0), 0);
}

function freshSeed(): string {
  return globalThis.crypto?.randomUUID?.().slice(0, 8) ?? String(Date.now());
}

export const store = new GameStore(createSave(freshSeed()));

/** Поднять прошлое дело из IndexedDB, если оно есть. */
export async function bootstrap(): Promise<void> {
  const loaded = await loadGame(store.currentSlot);
  if (loaded) store.replace(loaded);
  store.settleOnLoad();
}

function readSlot(): number {
  try {
    const raw = Number(localStorage.getItem(SLOT_KEY));
    return raw >= 1 && raw <= 3 ? raw : 1;
  } catch {
    return 1;
  }
}

function writeSlot(slot: number): void {
  try {
    localStorage.setItem(SLOT_KEY, String(slot));
  } catch {
    // Приватный режим: слот не запомнится, но игра работает.
  }
}

/** Звук по действию: одна воронка, поэтому озвучка не разъедется по кнопкам. */
function cue(action: Action, next: SaveDoc): void {
  switch (action.type) {
    case 'walk':
      play('step');
      break;
    case 'sell':
    case 'money':
      play('coin');
      break;
    case 'job':
      play(action.outcome.success ? 'coin' : 'fail');
      break;
    case 'fight':
    case 'takeover':
      play(action.won ? 'hit' : 'fail');
      break;
    case 'raid':
    case 'hospital':
    case 'finishRun':
      play('fail');
      break;
    case 'sleep':
      play('night');
      break;
    default:
      play('ui');
  }
  if (next.outcome !== 'active') play('fail');
}
